function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return; var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { StyledModal, StyledScrollContainer, StyledModalIconContainer, StyledModalHeader, StyledSpinnerWrapper, StyledModalContent, StyledCloseButtonContainer } from './Modal.style';
import { ModalBackdrop } from '../common/ModalBackdrop/ModalBackdrop';
import { Portal } from '../Portal/Portal';
import { ButtonIcon, CloseIcon, ScreenReaderOnly, Paragraph as P, useClickOutside, H1, H2, H3 } from '../../';
import { Spinner } from '../Spinner';
import { backgroundScrollTether, tabTrap, focus } from '../../utils';
import { getCleanProps } from '../../utility/getCleanProps';
import { useForceRerender } from '../../hooks';
var componentId = 'Modal';
Modal.propTypes = {
  /** Id for this modal */
  id: PropTypes.string.isRequired,

  /** the modal content */
  children: PropTypes.node,

  /** content alignment left/middle */
  alignment: PropTypes.oneOf(['left', 'middle']),

  /** modal title in an h1 tag */
  title: PropTypes.string.isRequired,

  /** styling applied to modal's h1; it uses styles from NUIT H1 (light), H2 (light or bold) or H3 (bold) */
  titleVariant: PropTypes.oneOf(['h1', 'h2', 'h2Bold', 'h3']),

  /** apply mobile responsive typography - should only be used in mobile app contexts */
  titleShouldResize: PropTypes.bool,

  /** modal variant */
  variant: PropTypes.oneOf(['default', 'spinner']),

  /** size of the modal.
   * small is 600px width & 400px fixed height,
   * medium is 600px width & 400px min-height,
   * large is 900px width & 400px min-height;
   * these heights here include the mandatory variable-height title */
  size: PropTypes.oneOf(['small', 'medium', 'large']),

  /** whether to display an icon */
  icon: PropTypes.elementType,

  /** the color of the modal icon */
  iconColor: PropTypes.string,

  /** whether it has a close icon on the top right corner. If false, use beginClose/onClose to manage modal state */
  hasClose: PropTypes.bool,

  /** whether the modal can be closed when pressing the escape key on the keyboard */
  hasEscapeClose: PropTypes.bool,

  /** whether the modal can be closed when clicking on the background */
  hasBackdropClose: PropTypes.bool,

  /** begins the closing animation. When the animation ends, it will close the modal. Used in custom close buttons. */
  beginClose: PropTypes.bool,

  /** callback function when the close animation ends. Use this to manage the state of the modal in your component. */
  onClose: PropTypes.func,

  /** choose where to append the modal in the dom. Sibling elements within the root node are tab trapped. */
  rootNode: PropTypes.oneOfType([PropTypes.instanceOf(typeof Element !== 'undefined' && Element), PropTypes.any]) // Todo: any = instanceOf(ShadowRoot)

};
Modal.defaultProps = {
  hasClose: false,
  size: 'medium',
  titleVariant: 'h1',
  titleShouldResize: false,
  variant: 'default',
  iconColor: 'primary',
  alignment: 'left',
  rootNode: typeof document !== 'undefined' && document.body
};
Modal.componentId = componentId;
export function Modal(_ref) {
  var id = _ref.id,
      children = _ref.children,
      beginClose = _ref.beginClose,
      alignment = _ref.alignment,
      variant = _ref.variant,
      size = _ref.size,
      title = _ref.title,
      titleVariant = _ref.titleVariant,
      titleShouldResize = _ref.titleShouldResize,
      Icon = _ref.icon,
      iconColor = _ref.iconColor,
      hasClose = _ref.hasClose,
      onClose = _ref.onClose,
      hasBackdropClose = _ref.hasBackdropClose,
      hasEscapeClose = _ref.hasEscapeClose,
      rootNode = _ref.rootNode,
      restProps = _objectWithoutProperties(_ref, ["id", "children", "beginClose", "alignment", "variant", "size", "title", "titleVariant", "titleShouldResize", "icon", "iconColor", "hasClose", "onClose", "hasBackdropClose", "hasEscapeClose", "rootNode"]);

  var _useState = useState(true),
      _useState2 = _slicedToArray(_useState, 2),
      isActive = _useState2[0],
      setIsActive = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      localBeginClose = _useState4[0],
      setLocalBeginClose = _useState4[1];

  var forceRerender = useForceRerender();
  var modalRef = useRef();
  var headingRef = useRef();
  var rootRef = useRef();

  var handleEscapeClose = function handleEscapeClose(event) {
    return hasEscapeClose && /^Esc/.test(event.key) && setLocalBeginClose(true);
  };

  var handleBackdropClose = function handleBackdropClose() {
    return hasBackdropClose && setLocalBeginClose(true);
  };

  useEffect(function () {
    backgroundScrollTether(isActive);
    tabTrap(isActive, rootRef.current, rootNode);
    setTimeout(function () {
      focus(headingRef.current, true);
    }, 100);
    return function () {
      backgroundScrollTether(false);
      tabTrap(false, rootRef.current, rootNode);
    };
  }, [isActive, variant, rootNode]);
  useClickOutside(modalRef, handleBackdropClose);

  var handleClose = function handleClose(event) {
    if (beginClose || localBeginClose) {
      setIsActive(false);
      /** A11y Hack
       * There's a bug in NVDA where it cannot re-read focused content if siblings/parents have aria-hidden=true.
       * NVDA stores its own reference of the element's focus state, and React does a delta before any re-render,
       * preventing NVDA from updating its focus state. This force re-render resets the reference (to the whole tree),
       * so screen readers can re-read focused content again.
       * I could be wrong about the details, but it definitely behaves differently when this is added!!
       * https://github.com/nvaccess/nvda/issues/5825
       * */

      forceRerender();
      if (onClose) onClose(event);
    }
  };

  var Heading = {
    h1: H1,
    h2: H2,
    h2Bold: H2,
    h3: H3
  }[titleVariant]; // only applied when bold H2 variant is required for mobile native apps - not to be used for browser apps

  var useBold = titleVariant === 'h2Bold' ? 'bold' : undefined;

  if (!isActive) {
    return null;
  }

  return /*#__PURE__*/React.createElement(Portal, {
    node: rootNode
  }, /*#__PURE__*/React.createElement(ModalBackdrop, {
    ref: rootRef,
    onKeyUp: handleEscapeClose
  }, variant === 'spinner' && /*#__PURE__*/React.createElement(ScreenReaderOnly, {
    textRef: headingRef
  }, "Loading, please wait"), /*#__PURE__*/React.createElement(StyledScrollContainer, null, /*#__PURE__*/React.createElement(StyledModal, _extends({}, getCleanProps(restProps), {
    ref: modalRef,
    id: id,
    size: size,
    beginClose: beginClose || localBeginClose,
    "data-component-id": componentId,
    onAnimationEnd: handleClose,
    tabIndex: -1,
    role: 'dialog',
    "aria-modal": true
  }), hasClose && /*#__PURE__*/React.createElement(StyledCloseButtonContainer, null, /*#__PURE__*/React.createElement(ButtonIcon, {
    label: 'Close message',
    color: 'black',
    icon: CloseIcon,
    onClick: function onClick() {
      return setLocalBeginClose(true);
    }
  })), variant === 'default' && Icon && /*#__PURE__*/React.createElement(StyledModalIconContainer, {
    alignment: alignment,
    size: size,
    variant: variant,
    hasIcon: !!Icon
  }, /*#__PURE__*/React.createElement(Icon, {
    color: iconColor,
    size: 'xxxlg'
  })), /*#__PURE__*/React.createElement(StyledModalContent, {
    alignment: alignment,
    size: size,
    variant: variant,
    hasIcon: !!Icon
  }, variant === 'spinner' ? /*#__PURE__*/React.createElement(StyledSpinnerWrapper, null, /*#__PURE__*/React.createElement(Spinner, {
    size: 'xl'
  }), /*#__PURE__*/React.createElement(P, null, "Loading")) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(StyledModalHeader, {
    alignment: alignment,
    variant: variant,
    titleVariant: titleVariant
  }, /*#__PURE__*/React.createElement(Heading, {
    variant: useBold,
    textRef: headingRef,
    as: 'h1',
    shouldResize: titleShouldResize
  }, /*#__PURE__*/React.createElement(ScreenReaderOnly, null, "Popup displayed, "), title)), children)), /*#__PURE__*/React.createElement(ScreenReaderOnly, null, "end of pop up")))));
}